import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import type { AddCommentFormData } from './types'

interface AddCommentDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: AddCommentFormData) => Promise<void> | void
}

const VISIBILITY_OPTIONS = [
  { value: false, label: 'Public', description: 'Visible to customer' },
  { value: true,  label: 'Internal', description: 'Staff only' },
] as const

const AUTO_CLOSE_MS = 1500

export function AddCommentDialog({ open, onClose, onSubmit }: AddCommentDialogProps) {
  const [comment, setComment]       = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [isLoading, setIsLoading]   = useState(false)
  const [status, setStatus]         = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setComment('')
      setIsInternal(false)
      setIsLoading(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open])

  const isValid = comment.trim().length > 0

  const handleSubmit = async () => {
    if (!isValid || isLoading) return
    setIsLoading(true)
    try {
      await onSubmit({ comment: comment.trim(), isInternal })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-[480px]">
        <DialogResultTransition
          status={status}
          successMessage="Comment added successfully"
          errorMessage="Failed to add comment"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >
          <DialogHeader>
            <DialogTitle>Add comment</DialogTitle>
            <DialogDescription>
              Leave a note or comment on the activity timeline.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Visibility */}
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-foreground">
                Visibility
              </label>
              <Select
                value={isInternal ? 'internal' : 'public'}
                onValueChange={(val) => setIsInternal(val === 'internal')}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map(({ value, label, description }) => (
                    <SelectItem key={String(value)} value={value ? 'internal' : 'public'}>
                      {label} — {description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Comment textarea */}
            <div className="space-y-2">
              <label className="text-[13px] font-medium text-foreground">
                Comment
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a note or comment about this payment..."
                rows={4}
                className="w-full rounded-xl border border-input bg-card px-3.5 py-3 text-[14px] resize-none outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
                aria-label="Comment text"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose} className="font-medium">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isLoading}
              className="px-6"
            >
              {isLoading ? 'Adding...' : 'Add comment'}
            </Button>
          </DialogFooter>
        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
